// src/components/property/ImageToVideoPromptModal.tsx
/**
 * ImageToVideoPromptModal Component
 * Modal for editing and regenerating image-to-video prompts with AI assistance
 */

import React, { memo, useState, useCallback, useEffect } from 'react';
import { Sparkles, Loader2, Save, Video, Info } from 'lucide-react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import type { IImageToVideoPromptModalProps } from '@/@types/propertySceneEditor';
import type { Id } from '../../../convex/_generated/dataModel';

// ============================================================================
// Helper Functions
// ============================================================================

function formatSceneType(type: string): string {
  return type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ');
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength) + '...';
}

// ============================================================================
// Tips Section Component
// ============================================================================

function PromptTips() {
  return (
    <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 p-4 text-sm">
      <div className="flex items-start gap-2">
        <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="space-y-2">
          <p className="font-medium text-blue-900 dark:text-blue-100">Tips for Better Prompts</p>
          <ul className="space-y-1 text-blue-800 dark:text-blue-200">
            <li>
              <span className="font-medium">Camera motion:</span> Describe movements like &quot;slow
              zoom in&quot;, &quot;pan left to right&quot;, or &quot;dolly forward&quot;
            </li>
            <li>
              <span className="font-medium">Timing:</span> Specify duration hints like
              &quot;gradual&quot;, &quot;smooth&quot;, or &quot;dynamic&quot;
            </li>
            <li>
              <span className="font-medium">Focus:</span> Mention what to highlight: &quot;focus on
              the kitchen island&quot; or &quot;emphasize natural lighting&quot;
            </li>
            <li>
              <span className="font-medium">Atmosphere:</span> Add mood descriptors like
              &quot;cinematic&quot;, &quot;peaceful&quot;, or &quot;inviting&quot;
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Scene Context Component
// ============================================================================

interface ISceneContextProps {
  sceneType: string;
  sceneNumber: number;
  voiceoverText: string;
}

function SceneContext({ sceneType, sceneNumber, voiceoverText }: ISceneContextProps) {
  return (
    <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Badge variant="secondary">Scene {sceneNumber}</Badge>
        <Badge variant="outline">{formatSceneType(sceneType)}</Badge>
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Voiceover Preview:</Label>
        <p className="text-sm mt-1 italic text-muted-foreground">
          &quot;{truncateText(voiceoverText, 150)}&quot;
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

// Interface for template data
interface IPromptTemplate {
  _id: Id<'promptTemplates'>;
  name: string;
  positivePrompt: string;
  negativePrompt: string;
}

function ImageToVideoPromptModal({
  isOpen,
  onClose,
  scene,
  onSave,
  onSaveAndGenerate,
  onRegeneratePrompt,
  isRegenerating,
  effectiveNegativePrompt,
}: IImageToVideoPromptModalProps) {
  // Local state for prompt text
  const [positivePrompt, setPositivePrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingAndGenerating, setIsSavingAndGenerating] = useState(false);

  // Default negative prompt
  const DEFAULT_NEGATIVE = 'blurry, distorted, low quality, amateur, shaky camera';

  // Fetch template data when scene has a template selected (supports both system and user templates)
  const template = useQuery(
    api.property.promptTemplates.getUnified,
    scene?.promptTemplateId ? { templateId: scene.promptTemplateId } : 'skip'
  ) as IPromptTemplate | null | undefined;

  // Initialize prompts from scene data when modal opens or scene changes
  useEffect(() => {
    if (scene && isOpen) {
      // Positive prompt priority: user edit > template > legacy prompt
      const initialPositive =
        scene.editedImagePrompt ?? template?.positivePrompt ?? scene.imageToVideoPrompt ?? '';
      setPositivePrompt(initialPositive);

      // Negative prompt priority: effectiveNegativePrompt prop > user edit > template > default
      const initialNegative =
        effectiveNegativePrompt ??
        scene.editedNegativePrompt ??
        template?.negativePrompt ??
        DEFAULT_NEGATIVE;
      setNegativePrompt(initialNegative);
    }
  }, [scene, isOpen, effectiveNegativePrompt, template]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsSaving(false);
      setIsSavingAndGenerating(false);
    }
  }, [isOpen]);

  // Handle positive prompt change
  const handlePositivePromptChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPositivePrompt(e.target.value);
  }, []);

  // Handle negative prompt change
  const handleNegativePromptChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNegativePrompt(e.target.value);
  }, []);

  // Handle save only
  const handleSave = useCallback(async () => {
    if (!positivePrompt.trim()) return;

    setIsSaving(true);
    try {
      await onSave(positivePrompt.trim(), negativePrompt.trim());
      onClose();
    } catch {
      // Error handling is done in parent component
    } finally {
      setIsSaving(false);
    }
  }, [positivePrompt, negativePrompt, onSave, onClose]);

  // Handle save and generate video
  const handleSaveAndGenerate = useCallback(async () => {
    if (!positivePrompt.trim()) return;

    setIsSavingAndGenerating(true);
    try {
      await onSaveAndGenerate(positivePrompt.trim(), negativePrompt.trim());
      onClose();
    } catch {
      // Error handling is done in parent component
    } finally {
      setIsSavingAndGenerating(false);
    }
  }, [positivePrompt, negativePrompt, onSaveAndGenerate, onClose]);

  // Handle AI regeneration
  const handleRegenerate = useCallback(async () => {
    await onRegeneratePrompt();
  }, [onRegeneratePrompt]);

  // Handle modal close (prevent close during operations)
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isSaving && !isSavingAndGenerating && !isRegenerating) {
        onClose();
      }
    },
    [isSaving, isSavingAndGenerating, isRegenerating, onClose]
  );

  // Derived state
  const isProcessing = isSaving || isSavingAndGenerating || isRegenerating;
  const hasValidPrompt = positivePrompt.trim().length > 0;
  const voiceoverText =
    scene?.editedVoiceoverText ?? scene?.voiceoverText ?? 'No voiceover text available';

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Video Prompts</DialogTitle>
          <DialogDescription>
            Describe how the camera should move and what visual dynamics to apply to this scene
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Scene Context */}
          {scene && (
            <SceneContext
              sceneType={scene.sceneType}
              sceneNumber={scene.sceneNumber}
              voiceoverText={voiceoverText}
            />
          )}

          {/* Positive Prompt Editor */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="positive-prompt">Positive Prompt</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRegenerate}
                disabled={isProcessing}
              >
                {isRegenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Regenerating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-1" />
                    AI Regenerate
                  </>
                )}
              </Button>
            </div>
            <Textarea
              id="positive-prompt"
              value={positivePrompt}
              onChange={handlePositivePromptChange}
              placeholder="Describe camera movement and visual dynamics... e.g., 'Slow dolly forward revealing the spacious living room with soft ambient lighting, smooth cinematic motion'"
              rows={5}
              className="resize-none"
              disabled={isProcessing}
            />
            <p className="text-xs text-muted-foreground">{positivePrompt.length} characters</p>
          </div>

          {/* Negative Prompt Editor */}
          <div className="space-y-2">
            <Label htmlFor="negative-prompt">Negative Prompt</Label>
            <Textarea
              id="negative-prompt"
              value={negativePrompt}
              onChange={handleNegativePromptChange}
              placeholder="Things to avoid... e.g., 'blurry, distorted, low quality, amateur, shaky camera'"
              rows={3}
              className="resize-none"
              disabled={isProcessing}
            />
            <p className="text-xs text-muted-foreground">
              Specify what you don&apos;t want in the video
            </p>
          </div>

          {/* Tips Section */}
          <PromptTips />
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={handleSave}
            disabled={!hasValidPrompt || isProcessing}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-1" />
                Save Only
              </>
            )}
          </Button>
          <Button onClick={handleSaveAndGenerate} disabled={!hasValidPrompt || isProcessing}>
            {isSavingAndGenerating ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Video className="h-4 w-4 mr-1" />
                Save & Generate Video
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const ImageToVideoPromptModalMemo = memo(ImageToVideoPromptModal);
